import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ComparisonChart from '../components/ComparisonChart';

const SharedComprehensiveReport = () => {
  const { reportId, accountName, dateRange, timestamp } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('topPerformers');
  const [topPerformersSort, setTopPerformersSort] = useState('roas');
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    assetUrl: '',
    adName: '',
    mediaFormat: ''
  });

  // Fetch shared report data
  useEffect(() => {
    const fetchSharedReport = async () => {
      try {
        setLoading(true);
        
        let apiUrl;
        
        // Check if we have the new URL structure (accountName/dateRange/timestamp)
        if (accountName && dateRange && timestamp) {
          apiUrl = `/api/reports/${accountName}/${dateRange}/${timestamp}`;
        } else {
          // Fallback to legacy ID-based URL
          apiUrl = `/api/reports/${reportId}`;
        }
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error('Report not found');
        }
        
        const result = await response.json();
        setReportData(result.report);
      } catch (error) {
        console.error('Error fetching shared report:', error);
        setError(error.message);
        toast.error('Failed to load shared report');
      } finally {
        setLoading(false);
      }
    };

    if ((accountName && dateRange && timestamp) || reportId) {
      fetchSharedReport();
    }
  }, [reportId, accountName, dateRange, timestamp]);

  // Tab configuration (only 3 tabs as requested)
  const tabs = [
    { id: 'topPerformers', label: 'Top Performers', icon: '🏆' },
    { id: 'copyThemes', label: 'Copy Themes', icon: '✍️' },
    { id: 'adTypeAnalysis', label: 'Ad Type Analysis', icon: '📱' }
  ];

  // Render thumbnail component
  const renderThumbnail = (assetUrl, mediaFormat, adName, className = "w-16 h-16") => {
    if (!assetUrl) {
      return (
        <div className={`${className} bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs`}>
          No Asset
        </div>
      );
    }

    const openModal = () => {
      setPreviewModal({
        isOpen: true,
        assetUrl,
        adName,
        mediaFormat
      });
    };

    if (mediaFormat === 'Video') {
      return (
        <div className={`${className} relative bg-gray-200 rounded overflow-hidden cursor-pointer`} onClick={openModal}>
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
          <div 
            className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-xs hidden"
          >
            Click to View
          </div>
        </div>
      );
    }

    return (
      <img
        src={assetUrl}
        alt={adName}
        className={`${className} object-cover rounded cursor-pointer hover:opacity-80 transition-opacity`}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
        onClick={openModal}
      />
    );
  };

  // Render top performers tab
  const renderTopPerformers = () => {
    // Try both data access patterns
    const topPerformers = reportData?.report_data?.topPerformers || 
                          reportData?.topPerformers;
    
    if (!topPerformers) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏆</div>
          <p className="text-gray-500 text-lg">No top performers data available.</p>
        </div>
      );
    }

    // Handle new data structure (byROAS/bySpend) or legacy array structure
    let topPerformersData = [];
    
    if (topPerformers.byROAS && topPerformers.bySpend) {
      // New structure: use appropriate dataset based on sort criteria
      if (topPerformersSort === 'roas') {
        topPerformersData = topPerformers.byROAS;
      } else {
        topPerformersData = topPerformers.bySpend;
      }
    } else if (Array.isArray(topPerformers)) {
      // Legacy structure: use array directly
      topPerformersData = topPerformers;
    } else {
      // Fallback: empty array
      topPerformersData = [];
    }

    if (!topPerformersData || topPerformersData.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🏆</div>
          <p className="text-gray-500 text-lg">No top performers data available.</p>
        </div>
      );
    }

    // Filter out ads without assets
    const sortedTopPerformers = topPerformersData
      .filter(ad => ad.asset_url && ad.asset_url.trim() !== '')
      .slice(0, 10); // Take top 10

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">🏆 Top Performing Ads</h3>
            
            {/* Sort Toggle */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Sort by:</span>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setTopPerformersSort('roas')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    topPerformersSort === 'roas'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Top ROAS
                </button>
                <button
                  onClick={() => setTopPerformersSort('spend')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    topPerformersSort === 'spend'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Top Spend
                </button>
              </div>
            </div>
          </div>

          {sortedTopPerformers.length > 0 ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-500 mb-2">
                Showing top 10 ads by {topPerformersSort === 'roas' ? 'ROAS (Return on Ad Spend)' : 'Spend (Total Investment)'}
              </div>
              
              {sortedTopPerformers.map((ad, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start space-x-4">
                    {/* Rank Badge */}
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-800' :
                        index === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                    
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {renderThumbnail(ad.asset_url, ad.media_format, ad.ad_name, "w-20 h-20")}
                    </div>
                    
                    {/* Ad Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900">{ad.ad_name}</h4>
                          <p className="text-sm text-gray-500">{ad.ad_type}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            ad.media_format === 'Video' ? 'bg-blue-100 text-blue-800' :
                            ad.media_format === 'Image' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {ad.media_format}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            topPerformersSort === 'roas' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {topPerformersSort === 'roas' ? `${ad.roas}x ROAS` : `$${parseFloat(ad.total_spend || 0).toLocaleString()}`}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">ROAS:</span>
                          <span className={`ml-2 font-medium ${
                            parseFloat(ad.roas || 0) >= 2.0 ? 'text-green-600' :
                            parseFloat(ad.roas || 0) >= 1.0 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {ad.roas}x
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">CTR:</span>
                          <span className="ml-2 font-medium">{ad.ctr}%</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Spend:</span>
                          <span className="ml-2 font-medium">${parseFloat(ad.total_spend || 0).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Features:</span>
                          <span className="ml-2 font-medium text-gray-700">
                            {[ad.product_position, ad.setting, ad.lighting]
                              .filter(feature => 
                                feature && 
                                feature !== 'Not Applicable' && 
                                feature !== 'N/A' && 
                                feature !== 'n/a' && 
                                !feature.toLowerCase().includes('not applicable') &&
                                !feature.toLowerCase().includes('n/a') &&
                                !feature.toLowerCase().includes('unknown')
                              )
                              .join(', ') || 'Features not specified'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Top Ads Comparison Chart */}
              {sortedTopPerformers.length > 0 && (
                <ComparisonChart
                  title={`Top Ads Comparison (by ${topPerformersSort === 'roas' ? 'ROAS' : 'Spend'})`}
                  data={sortedTopPerformers.map(ad => ({
                    name: (ad.ad_name || 'Unknown').substring(0, 30), // Truncate long names
                    spend: parseFloat(ad.total_spend || 0),
                    roas: parseFloat(ad.roas || 0)
                  }))}
                  xAxisKey="name"
                  metrics={[
                    { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                    { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                  ]}
                  height={400}
                  maxItems={10}
                />
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">🖼️</div>
              <p className="text-gray-500 text-lg mb-2">No ads with assets found</p>
              <p className="text-gray-400 text-sm">
                All top performing ads in this date range don't have associated images or videos.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render copy themes tab
  const renderCopyThemes = () => {
    // Try multiple data access patterns for copy content clustering
    let clusters = null;
    let rawCopyData = null;
    let copyError = null;
    
    // Pattern 1: report_data.copyContentClustering
    if (reportData?.report_data?.copyContentClustering?.clusters?.length > 0) {
      clusters = reportData.report_data.copyContentClustering.clusters;
      rawCopyData = reportData.report_data.copyContentClustering.rawCopyData;
    }
    // Pattern 2: direct copyContentClustering
    else if (reportData?.copyContentClustering?.clusters?.length > 0) {
      clusters = reportData.copyContentClustering.clusters;
      rawCopyData = reportData.copyContentClustering.rawCopyData;
    }
    // Check for error in copy content clustering
    else if (reportData?.report_data?.copyContentClustering?.error || reportData?.copyContentClustering?.error) {
      copyError = reportData?.report_data?.copyContentClustering?.error || reportData?.copyContentClustering?.error;
    }
    
    if (!clusters || clusters.length === 0) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-2">✍️ Copy Content Clustering Analysis</h3>
            <p className="text-sm text-gray-600 mb-4">Comprehensive creative analytics based on visual, audio and text in the ads</p>
            
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📝</div>
              <h4 className="text-lg font-medium text-gray-600 mb-2">Copy Analysis Not Available</h4>
              
              {copyError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-center mb-2">
                    <div className="text-red-500 text-sm font-medium">Analysis Failed</div>
                  </div>
                  <p className="text-red-600 text-sm">
                    The copy content clustering analysis encountered an error during report generation: <strong>{copyError}</strong>
                  </p>
                  <p className="text-red-500 text-xs mt-2">
                    This usually happens due to network timeouts or service unavailability. Please try generating a new report.
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <p className="text-gray-600 text-sm">
                    No copy themes were generated for this report. This could be due to:
                  </p>
                  <ul className="text-gray-500 text-xs mt-2 space-y-1">
                    <li>• Insufficient ad copy data in the selected date range</li>
                    <li>• Analysis service was unavailable during report generation</li>
                    <li>• Network connectivity issues</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    const totalAds = clusters.reduce((sum, cluster) => sum + (cluster.ads?.length || 0), 0);
    const avgROAS = clusters.reduce((sum, cluster) => sum + (cluster.performance?.avg_roas || 0), 0) / clusters.length;
    const avgCTR = clusters.reduce((sum, cluster) => sum + (cluster.performance?.avg_ctr || 0), 0) / clusters.length;

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-2">✍️ Copy Content Clustering Analysis</h3>
          <p className="text-sm text-gray-600 mb-4">Comprehensive creative analytics based on visual, audio and text in the ads</p>
          
          {/* Data Coverage Summary */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-3">📊 Data Coverage Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{clusters.length}</div>
                <div className="text-xs text-blue-600">Copy Themes</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{totalAds}</div>
                <div className="text-xs text-blue-600">Total Ads</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{avgROAS.toFixed(1)}x</div>
                <div className="text-xs text-blue-600">Avg ROAS</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{avgCTR.toFixed(1)}%</div>
                <div className="text-xs text-blue-600">Avg CTR</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            {clusters.map((cluster, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{cluster.theme_name}</h4>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    {cluster.ads?.length || 0} ads
                  </span>
                </div>
                
                {/* Cluster Performance Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                  <div className="text-center">
                    <div className="text-gray-500 text-xs">ROAS</div>
                    <div className="font-medium text-gray-900">{cluster.performance?.avg_roas || 'N/A'}x</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500 text-xs">CTR</div>
                    <div className="font-medium text-gray-900">{cluster.performance?.avg_ctr || 'N/A'}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500 text-xs">CVR</div>
                    <div className="font-medium text-gray-900">{cluster.performance?.avg_cvr || 'N/A'}%</div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500 text-xs">Spend</div>
                    <div className="font-medium text-gray-900">${parseFloat(cluster.performance?.total_spend || 0).toLocaleString()}</div>
                  </div>
                </div>

                {/* Sample Ads in Cluster */}
                {cluster.ads && cluster.ads.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs text-gray-500 mb-2">Sample Ads in this theme:</div>
                    <div className="flex flex-wrap gap-2">
                      {cluster.ads.slice(0, 5).map((adId, adIndex) => {
                        const adData = rawCopyData?.find(ad => ad.raw_ad_id === adId);
                        return (
                          <span key={adIndex} className="px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-700">
                            {adData?.ad_name || adId}
                          </span>
                        );
                      })}
                      {cluster.ads.length > 5 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded text-xs">
                          +{cluster.ads.length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Copy Analytics Grid */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">📊 Copy Analytics for this Theme</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Top Hooks */}
                    {cluster.top_hooks && cluster.top_hooks.length > 0 && cluster.top_hooks.some(hook => 
                      hook.performance && !hook.performance.includes('0x ROAS') && !hook.performance.includes('0% CTR') && !hook.performance.includes('0% CVR')
                    ) && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-blue-800 mb-3">🎯 Top Hooks</h4>
                        <div className="space-y-2">
                          {cluster.top_hooks.slice(0, 3).map((hook, idx) => (
                            <div key={idx} className="bg-white rounded border border-blue-200 p-3">
                              <p className="text-xs text-gray-800 font-medium mb-1">"{hook.hook}"</p>
                              <p className="text-xs text-blue-600">{hook.performance}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top CTAs */}
                    {cluster.top_ctas && cluster.top_ctas.length > 0 && cluster.top_ctas.some(cta => 
                      cta.performance && !cta.performance.includes('0x ROAS') && !cta.performance.includes('0% CTR') && !cta.performance.includes('0% CVR')
                    ) && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-orange-800 mb-3">📢 Top CTAs</h4>
                        <div className="space-y-2">
                          {cluster.top_ctas.slice(0, 3).map((cta, idx) => (
                            <div key={idx} className="bg-white rounded border border-orange-200 p-3">
                              <p className="text-xs text-gray-800 font-medium mb-1">"{cta.cta}"</p>
                              <p className="text-xs text-orange-600">{cta.performance}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Offers */}
                    {cluster.top_offers && cluster.top_offers.length > 0 && cluster.top_offers.some(offer => 
                      offer.performance && !offer.performance.includes('0x ROAS') && !offer.performance.includes('0% CTR') && !offer.performance.includes('0% CVR')
                    ) && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-purple-800 mb-3">💰 Top Offers</h4>
                        <div className="space-y-2">
                          {cluster.top_offers.slice(0, 3).map((offer, idx) => (
                            <div key={idx} className="bg-white rounded border border-purple-200 p-3">
                              <p className="text-xs text-gray-800 font-medium mb-1">"{offer.offer}"</p>
                              <p className="text-xs text-purple-600">{offer.performance}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sales Messaging */}
                    {cluster.sales_messaging && cluster.sales_messaging.length > 0 && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-teal-800 mb-3">💡 Sales Messaging Patterns</h4>
                        <div className="space-y-2">
                          {cluster.sales_messaging.slice(0, 3).map((pattern, idx) => (
                            <div key={idx} className="bg-white rounded border border-teal-200 p-3">
                              <p className="text-xs text-gray-800">{pattern}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Copy Examples with Thumbnails */}
                {cluster.copy_examples && cluster.copy_examples.length > 0 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <h5 className="text-sm font-medium text-green-800 mb-2">✍️ Copy Examples</h5>
                    <div className="space-y-3">
                      {cluster.copy_examples.map((example, idx) => {
                        const adData = rawCopyData?.find(ad => ad.ad_name === example.ad_name);
                        return (
                          <div key={idx} className="p-3 bg-white rounded border border-green-200">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                {renderThumbnail(adData?.asset_url, adData?.media_format, example.ad_name, "w-16 h-16")}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start mb-2">
                                  <h6 className="text-sm font-medium text-green-700">{example.ad_name}</h6>
                                  <div className="text-xs text-green-600">
                                    ROAS: {example.performance?.roas && typeof example.performance.roas === 'number' 
                                      ? example.performance.roas.toFixed(2) 
                                      : example.performance?.roas || 'N/A'}x | 
                                    CTR: {example.performance?.ctr && typeof example.performance.ctr === 'number' 
                                      ? example.performance.ctr.toFixed(2) 
                                      : example.performance?.ctr || 'N/A'}% | 
                                    CVR: {example.performance?.cvr && typeof example.performance.cvr === 'number' 
                                      ? example.performance.cvr.toFixed(2) 
                                      : example.performance?.cvr || 'N/A'}%
                                  </div>
                                </div>
                                
                                {example.headline && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-600">Headline:</span>
                                    <p className="text-sm text-gray-800 mt-1">{example.headline}</p>
                                  </div>
                                )}
                                
                                {example.primary_text && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-600">Primary Text:</span>
                                    <p className="text-sm text-gray-800 mt-1">{example.primary_text}</p>
                                  </div>
                                )}
                                
                                {example.description && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-600">Description:</span>
                                    <p className="text-sm text-gray-800 mt-1">{example.description}</p>
                                  </div>
                                )}
                                
                                {example.audio_text && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-600">Audio Text:</span>
                                    <p className="text-sm text-gray-800 mt-1">{example.audio_text}</p>
                                  </div>
                                )}
                                
                                {example.text_overlay && (
                                  <div className="mb-2">
                                    <span className="text-xs font-medium text-gray-600">Text Overlay:</span>
                                    <p className="text-sm text-gray-800 mt-1">{example.text_overlay}</p>
                                  </div>
                                )}
                                

                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* AI Insights Section */}
          {(() => {
            // Try multiple data access patterns for insights
            const insights = reportData?.report_data?.copyContentClustering?.insights || 
                           reportData?.copyContentClustering?.insights || 
                           [];
            
            const recommendations = reportData?.report_data?.copyContentClustering?.recommendations || 
                                 reportData?.copyContentClustering?.recommendations || 
                                 [];
            
            return (insights.length > 0 || recommendations.length > 0) ? (
              <div className="space-y-4">
                {/* AI Insights */}
                {insights.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="font-medium text-yellow-800 mb-2">💡 AI Insights</h5>
                    <ul className="space-y-1 text-sm text-yellow-700">
                      {insights.map((insight, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-yellow-600 mr-2">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* AI Recommendations */}
                {recommendations.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h5 className="font-medium text-green-800 mb-2">🚀 AI Recommendations</h5>
                    <ul className="space-y-1 text-sm text-green-700">
                      {recommendations.map((rec, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-green-600 mr-2">→</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null;
          })()}

        </div>
      </div>
    );
  };

  // Render ad type analysis tab
  const renderAdTypeAnalysis = () => {
    // Try both data access patterns
    const adTypes = reportData?.report_data?.adTypeAnalysis || 
                    reportData?.adTypeAnalysis;
    
    // Filter out unknown or non-applicable ad types for cleaner UI
    const filteredAdTypes = (adTypes || []).filter(type => 
      type.ad_type && 
      type.ad_type !== 'Unknown' && 
      type.ad_type !== 'N/A' && 
      type.ad_type !== 'n/a' && 
      !type.ad_type.toLowerCase().includes('unknown') &&
      !type.ad_type.toLowerCase().includes('n/a') &&
      !type.ad_type.toLowerCase().includes('not applicable')
    );
    
    if (!filteredAdTypes || filteredAdTypes.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📱</div>
          <p className="text-gray-500 text-lg">No ad type analysis data available.</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">📱 Ad Type Performance Analysis</h3>
          {filteredAdTypes && filteredAdTypes.length > 0 ? (
            <div className="space-y-6">
              {/* Performance Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {filteredAdTypes.length}
                  </div>
                  <div className="text-sm text-blue-700">Ad Types</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    ${filteredAdTypes.reduce((sum, type) => sum + parseFloat(type.total_spend || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-green-700">Total Spend</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">
                    {filteredAdTypes.reduce((sum, type) => sum + parseInt(type.number_of_ads || 0), 0)}
                  </div>
                  <div className="text-sm text-purple-700">Total Ads</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">
                    ${filteredAdTypes.reduce((sum, type) => sum + parseFloat(type.total_revenue || 0), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-yellow-700">Total Revenue</div>
                </div>
              </div>

              {/* Top vs Bottom Performers */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top Performing Ad Types */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-green-800 mb-4">🏆 Top Performing Ad Types (Top 50%)</h4>
                  <div className="space-y-3">
                    {(() => {
                      const validAdTypes = filteredAdTypes.filter(type => parseFloat(type.roas || 0) > 0);
                      const sortedAdTypes = validAdTypes.sort((a, b) => parseFloat(b.roas || 0) - parseFloat(a.roas || 0));
                      const midPoint = Math.ceil(sortedAdTypes.length / 2);
                      const topHalf = sortedAdTypes.slice(0, midPoint);
                      
                      return topHalf.map((type, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded border border-green-200">
                          <div>
                            <div className="font-medium text-green-700">{type.ad_type}</div>
                            <div className="text-sm text-green-600">{type.number_of_ads} ads</div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-lg font-bold text-green-800">{type.roas}x ROAS</span>
                              {type.benchmark && type.benchmark.avg_roas && (() => {
                                const roasDiff = ((parseFloat(type.roas) - parseFloat(type.benchmark.avg_roas)) / parseFloat(type.benchmark.avg_roas) * 100).toFixed(1);
                                const ctrDiff = ((parseFloat(type.ctr) - parseFloat(type.benchmark.avg_ctr || 0)) / parseFloat(type.benchmark.avg_ctr || 1) * 100).toFixed(1);
                                const roasSign = roasDiff >= 0 ? '+' : '';
                                const ctrSign = ctrDiff >= 0 ? '+' : '';
                                
                                return (
                                  <div className="relative group">
                                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs cursor-help hover:bg-blue-200 transition-colors">
                                      ℹ️
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                      <div className="font-medium mb-1">vs Platform Average:</div>
                                      <div className={roasDiff >= 0 ? 'text-green-300' : 'text-red-300'}>ROAS: {roasSign}{roasDiff}% (avg: {type.benchmark.avg_roas}x)</div>
                                      <div className={ctrDiff >= 0 ? 'text-green-300' : 'text-red-300'}>CTR: {ctrSign}{ctrDiff}% (avg: {type.benchmark.avg_ctr || 'N/A'}%)</div>
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="text-sm text-green-600">
                              {type.ctr}% CTR
                              {type.media_type && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                  {type.media_type}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>

                {/* Bottom Performing Ad Types - Bottom Half by ROAS */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-lg font-medium text-red-800 mb-4">⚠️ Underperforming Ad Types (Bottom 50%)</h4>
                  <div className="space-y-3">
                    {(() => {
                      const validAdTypes = filteredAdTypes.filter(type => parseFloat(type.roas || 0) > 0);
                      const sortedAdTypes = validAdTypes.sort((a, b) => parseFloat(b.roas || 0) - parseFloat(a.roas || 0));
                      const midPoint = Math.ceil(sortedAdTypes.length / 2);
                      const bottomHalf = sortedAdTypes.slice(midPoint);
                      
                      return bottomHalf.map((type, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded border border-red-200">
                          <div>
                            <div className="font-medium text-red-700">{type.ad_type}</div>
                            <div className="text-sm text-red-600">{type.number_of_ads} ads</div>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <span className="text-lg font-bold text-red-800">{type.roas}x ROAS</span>
                              {type.benchmark && type.benchmark.avg_roas && (() => {
                                const roasDiff = ((parseFloat(type.roas) - parseFloat(type.benchmark.avg_roas)) / parseFloat(type.benchmark.avg_roas) * 100).toFixed(1);
                                const ctrDiff = ((parseFloat(type.ctr) - parseFloat(type.benchmark.avg_ctr || 0)) / parseFloat(type.benchmark.avg_ctr || 1) * 100).toFixed(1);
                                const roasSign = roasDiff >= 0 ? '+' : '';
                                const ctrSign = ctrDiff >= 0 ? '+' : '';
                                
                                return (
                                  <div className="relative group">
                                    <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs cursor-help hover:bg-blue-200 transition-colors">
                                      ℹ️
                                    </div>
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                      <div className="font-medium mb-1">vs Platform Average:</div>
                                      <div className={roasDiff >= 0 ? 'text-green-300' : 'text-red-300'}>ROAS: {roasSign}{roasDiff}% (avg: {type.benchmark.avg_roas}x)</div>
                                      <div className={ctrDiff >= 0 ? 'text-green-300' : 'text-red-300'}>CTR: {ctrSign}{ctrDiff}% (avg: {type.benchmark.avg_ctr || 'N/A'}%)</div>
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="text-sm text-red-600">
                              {type.ctr}% CTR
                              {type.media_type && (
                                <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                                  {type.media_type}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              </div>

              {/* Detailed Performance Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number of Ads</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ROAS</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTR</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CVR</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CPM</th>

                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Spend</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {adTypes.map((adType, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{adType.ad_type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adType.number_of_ads}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adType.roas}x</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adType.ctr}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{adType.cvr}%</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${adType.cpm}</td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${parseFloat(adType.total_spend || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${parseFloat(adType.total_revenue || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Ad Types Comparison Chart */}
              {adTypes.length > 0 && (
                <ComparisonChart
                  title="Ad Types Comparison"
                  data={adTypes.map(type => ({
                    name: type.ad_type || 'Unknown',
                    spend: parseFloat(type.total_spend || 0),
                    roas: parseFloat(type.roas || 0),
                    ctr: parseFloat(type.ctr || 0),
                    revenue: parseFloat(type.total_revenue || 0)
                  }))}
                  xAxisKey="name"
                  metrics={[
                    { key: 'spend', label: 'Spend', color: '#8b5cf6', format: 'currency', yAxisId: 'left' },
                    { key: 'roas', label: 'ROAS', color: '#ec4899', format: 'number', yAxisId: 'right' }
                  ]}
                  height={400}
                  maxItems={15}
                />
              )}
            </div>
          ) : (
            <p className="text-gray-500">No ad type analysis data available.</p>
          )}
        </div>
      </div>
    );
  };

  // Tab content renderer
  const renderTabContent = () => {
    switch (activeTab) {
      case 'topPerformers':
        return renderTopPerformers();
      case 'copyThemes':
        return renderCopyThemes();
      case 'adTypeAnalysis':
        return renderAdTypeAnalysis();
      default:
        return renderTopPerformers();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Report Not Found</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">
                {reportData.account_name ? `${reportData.account_name} Creative Analysis Report` : 'Creative Analysis Report'}
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              Shared on: {new Date(reportData.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          {renderTabContent()}
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-4" title={previewModal.adName}>
                {previewModal.adName}
              </h3>
              <button
                onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
                className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
                title="Close"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {previewModal.mediaFormat === 'Video' ? (
                <video 
                  className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                  controls
                  autoPlay
                  preload="metadata"
                  onError={(e) => {
                    console.error('Video preview error:', e);
                  }}
                >
                  <source src={previewModal.assetUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={previewModal.assetUrl} 
                  alt={previewModal.adName}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    console.error('Image preview error:', e);
                  }}
                />
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">
                  {previewModal.mediaFormat === 'Video' ? '🎥 Video Asset' : '🖼️ Image Asset'}
                </span>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
                    className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                  <a
                    href={previewModal.assetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded hover:bg-blue-50 transition-colors"
                  >
                    Open in New Tab →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedComprehensiveReport;
